import React, { useEffect, useState } from 'react';
import {
  Box,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Button,
  TextField,
  DialogTitle,
  DialogActions,
  IconButton,
  Dialog,
  LinearProgress,
  CircularProgress,
  DialogContent,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Map, useControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapboxOverlay } from '@deck.gl/mapbox';
import Lasso from './RoutingForm/Lasso';
import centerOfMass from '@turf/center-of-mass';
import produce from 'immer';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';

import { fitBoundPolygon } from './RoutingForm/Map';
import { getCourierZones, saveCourierZones, getRegionList } from '../repositories/HttpRepository';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    minWidth: 200,
  },
  mapWrapper: {
    position: 'relative',
  },
  cancelBtn: {
    border: 'solid 1px #4a4a4a',
    backgroundColor: '#ffffff',
    padding: '3px 15px',
    marginRight: 10,
  },
  saveBtn: {
    backgroundColor: '#4abc4e',
    padding: '4px 25px',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#368b39',
    },
    '&:disabled': {
      backgroundColor: '#d0d0d0',
    },
  },
  dialogAction: {
    padding: theme.spacing(1, 3),
    justifyContent: 'space-between',
  },
  noteInput: {
    margin: 0,
    marginRight: 5,
    width: 450,
  },
  nameInput: {
    marginTop: 10,
    fontSize: '14px',
  },
  redText: {
    color: 'red',
  },
}));
const mapStyle = `${process.env.REACT_APP_MAP_TILER_URI}?key=${process.env.REACT_APP_MAP_TILER_KEY}`;

function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

function CourierZone(props) {
  const { courier } = props;
  const [viewport, setViewport] = useState({
    latitude: 33.851029206367905,
    longitude: -118.38935852050781,
    zoom: 10,
    width: 800,
    height: 500,
  });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [lasso, setLasso] = useState(false);
  const [geojson, setGeojson] = useState([]);
  const [currentObject, setCurrentObject] = useState(null);
  const [region, setRegion] = useState('');
  const [regionNote, setRegionNote] = useState('');
  const [nameValue, setNameValue] = useState('');
  const [noteValue, setNoteValue] = useState('');
  const [error, setError] = useState('');
  const [regions, setRegions] = useState([]);

  const { classes } = useStyles();
  const [layers, setLayers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const [courierZones, regions] = await Promise.all([getCourierZones(courier.id), getRegionList()]);

      const dataParse = {};
      courierZones.data.forEach((rg) => {
        dataParse[rg.region] = rg;
      });
      const regionList = regions.data.map(({ properties }) => ({
        label: properties.display_name,
        value: properties.code,
      }));

      setData(dataParse);
      setRegions(regionList);
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (geojson.length > 0) {
      const polygonLayer = new GeoJsonLayer({
        id: 'polygon-layer',
        data: geojson,
        opacity: 0.5,
        pickable: true,
        stroked: false,
        filled: true,
        extruded: true,
        lineWidthScale: 20,
        lineWidthMinPixels: 2,
        getFillColor: [160, 160, 160, 150],
        getRadius: 100,
        getLineWidth: 1,
        getElevation: 1,
        onClick: (event) => {
          onGeoJsonClick(event);
          return true;
        },
      });
      const centerTextsLayer = new TextLayer({
        id: 'center-texts-layer',
        data: geojson,
        pickable: true,
        getPosition: (d) => d.center,
        getText: (d) => d.properties.zone_id || '',
        getColor: (d) => [127, 0, 255],
        getSize: (d) => 18,
        getAngle: 0,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
      });
      const poIndex = layers.findIndex((l) => l.id === 'polygon-layer');
      const textIndex = layers.findIndex((l) => l.id === 'center-texts-layer');
      const newLayers = produce(layers, (draft) => {
        if (poIndex > -1) {
          draft[poIndex] = polygonLayer;
        } else {
          draft.push(polygonLayer);
        }
        if (textIndex > -1) {
          draft[textIndex] = centerTextsLayer;
        } else {
          draft.push(centerTextsLayer);
        }
      });
      setLayers(newLayers);
      if (viewport.height) fitBoundPolygon(geojson, viewport, setViewport);
    } else {
      setLayers([]);
    }

    if (!!region) {
      const newData = produce(data, (draft) => {
        if (!draft[region]) draft[region] = {};
        draft[region].features = geojson;
        if (!!courier && !draft[region].name) {
          const name = `${courier.code}-${region}`;
          draft[region].name = name;
          draft[region].id = name.toLowerCase();
          draft[region].type = 'FeatureCollection';
          draft[region].region = region;
        }
      });
      setData(newData);
    }
  }, [geojson]);

  const changeRegion = (region) => {
    const { value, center } = region;

    if (!value) return;

    if (center) {
      setViewport({ ...viewport, latitude: center.lat, longitude: center.lng });
    }
    setRegion(value);
    if (data[value]) {
      setGeojson(data[value].features || []);
      setRegionNote(data[value].note || '');
    } else {
      setGeojson([]);
      setRegionNote('');
    }
  };

  const changeRegionNote = (e) => {
    setRegionNote(e.target.value);
    if (!!region) {
      const newData = produce(data, (draft) => {
        if (!draft[region]) draft[region] = {};
        draft[region].note = e.target.value;
      });
      setData(newData);
    }
  };

  const saveProperty = () => {
    if (!!currentObject) {
      const newGeojson = produce(geojson, (draft) => {
        draft.map((geo) => {
          if (geo.properties?.id === currentObject.object.properties?.id) {
            geo.properties.zone_id = nameValue;
            geo.properties.note = noteValue;
          }
        });
      });
      setGeojson(newGeojson);
    }
    closeDialog();
  };

  const saveRegion = () => {
    setSaving(true);
    setError('');
    saveCourierZones(courier.id, region, data[region]).then((res) => {
      if (!res.ok) {
        setError(res.data ? res.data.message : `Failed with code ${res.status}`);
      }
      if (res.duration < 1500) {
        setTimeout(() => setSaving(false), 1500);
      } else {
        setSaving(false);
      }
    });
  };

  const removeGeoJson = () => {
    if (!!currentObject) {
      const newGeojson = produce(geojson, (draft) => {
        return draft.filter((geo) => geo.properties.id !== currentObject.object.properties.id);
      });
      setGeojson(newGeojson);
    }
    closeDialog();
  };

  const updateLasso = (data) => {
    if (data.features && data.features.length > 0) {
      const center = centerOfMass(data.features[0]);
      const newGeojson = {
        ...data.features[0],
        center: center.geometry.coordinates,
        properties: {
          id: `id_${Date.now()}`,
        },
      };
      setGeojson(
        produce(geojson, (draft) => {
          draft.push(newGeojson);
        }),
      );
      setLasso(false);
    }
  };

  const onGeoJsonClick = (e) => {
    setNameValue(e?.object?.properties.zone_id || '');
    setNoteValue(e?.object?.properties.note || '');
    setOpenDialog(true);
    setCurrentObject(e);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setCurrentObject(null);
  };

  const handleKeyUp = (e) => {
    if (e.key === 's' || e.key === 'S') {
      setLasso(true);
      return;
    }
    if (e.which === 27) {
      setLasso(false);
    }
  };

  if (!courier) return null;
  if (!courier.code) {
    return (
      <Box align="center" sx={{ p: 2 }}>
        Please set alias for courier before setting zone map
      </Box>
    );
  }

  if (loading) {
    return (
      <Box align="center" sx={{ p: 10 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const workingZones = Object.values(data)
    .filter((rg) => !!rg.features.length)
    .map((rg) => rg.region)
    .join(', ');
  return (
    <Box sx={{ mb: 3 }}>
      <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Grid>
          <FormControl variant="outlined" disabled={false} className={classes.formControl}>
            <InputLabel>Region</InputLabel>
            <Select label="Region" fullWidth displayEmpty disabled={false} value={region} onChange={(e, rg) => changeRegion(rg.props.data)}>
              {regions.map((option) => (
                <MenuItem key={option.value} value={option.value} data={option}>
                  [{option.value}] {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <Box className={classes.redText}>Remember to save after done editing.</Box>
        </Grid>
        <Grid>
          <Box align="center" sx={{ py: 1 }}>
            <Box>Working zones</Box>
            <Box>
              <strong>{workingZones || 'N/A'}</strong>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ py: 1 }}>
        {region && (
          <Box onKeyUp={handleKeyUp} className={classes.mapWrapper}>
            <Map {...viewport} mapStyle={mapStyle} onMove={(e) => setViewport((prev) => ({ ...prev, ...e.viewState }))} style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}>
              <DeckGLOverlay layers={layers} />
            </Map>
            {lasso && <Lasso changeData={updateLasso} changeViewport={setViewport} viewport={viewport} height="500px" />}
          </Box>
        )}
        {!region && (
          <Box align="center" sx={{ pt: 15, pb: 45 }}>
            Select region first
          </Box>
        )}
        <Box style={{ minHeight: 5 }}>{saving && <LinearProgress color="primary" />}</Box>
      </Box>
      {!!region && (
        <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Grid>
            <Button className={classes.cancelBtn} onClick={() => setGeojson([])} color="inherit">
              Clear Map
            </Button>
          </Grid>
          <Grid>
            <TextField
              className={classes.noteInput}
              variant="outlined"
              margin="dense"
              placeholder="Region note"
              value={regionNote}
              onChange={changeRegionNote}
              slotProps={{
                htmlInput: {
                  style: { padding: '8px' },
                },
              }}
            />
            <Button className={classes.saveBtn} onClick={saveRegion} disabled={saving}>
              Save {region}
            </Button>
          </Grid>
        </Grid>
      )}
      {error && (
        <Box className={classes.redText} align="center" sx={{ pt: 1 }}>
          {error}
        </Box>
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} aria-labelledby="form-dialog-title" maxWidth="sm">
        <DialogTitle>Update zone</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            variant="outlined"
            label="Name"
            fullWidth
            className={classes.nameInput}
            onChange={(e) => setNameValue(e.target.value.trim().toUpperCase())}
            value={nameValue}
            slotProps={{
              htmlInput: { maxLength: 10 },
            }}
          />
          <TextField size="small" variant="outlined" label="Note" className={classes.nameInput} fullWidth multiline rows={3} onChange={(e) => setNoteValue(e.target.value)} value={noteValue} />
        </DialogContent>
        <DialogActions className={classes.dialogAction}>
          <IconButton className={classes.closeBtn} onClick={removeGeoJson} size="large">
            <DeleteIcon />
          </IconButton>
          <Box>
            <Button onClick={closeDialog} variant="outlined" color="secondary" style={{ marginRight: 5 }}>
              Cancel
            </Button>
            <Button onClick={saveProperty} variant="contained" color="primary" disableElevation>
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CourierZone;
